import { useCallback } from "react";
import { type Identifier, useDataProvider, useNotify } from "ra-core";
import { useQueryClient } from "@tanstack/react-query";

import type { CrmDataProvider } from "../providers/supabase/dataProvider";
import {
  planeRefusal,
  planeRefusalMessage,
} from "../providers/commons/planeKey";

// Push one task to Plane and say how it went. Shared by the task surfaces'
// button and the meeting page's, so a refusal reads the same wherever it
// happens. Resolves true when the task is now linked.
export const usePushTaskToPlane = () => {
  const dataProvider = useDataProvider<CrmDataProvider>();
  const notify = useNotify();
  const queryClient = useQueryClient();

  return useCallback(
    async (taskId: Identifier): Promise<boolean> => {
      let pushed = false;
      try {
        const { results } = await dataProvider.pushTasksToPlane([taskId]);
        const r = results?.[0];
        const refusal = planeRefusal(results);
        if (refusal) {
          notify(planeRefusalMessage(refusal), { type: "warning" });
        } else if (r?.ok) {
          pushed = true;
          notify("crm.plane.pushed", { type: "success" });
        } else if (r?.reason === "assignee_not_member") {
          const a = r.assignee;
          const who =
            a?.name && a?.email
              ? `${a.name} (${a.email})`
              : a?.name || a?.email || "This user";
          notify("crm.plane.assignee_not_on_plane", {
            type: "warning",
            messageArgs: { user: who },
          });
        } else {
          notify("crm.plane.push_failed", { type: "error" });
        }
        // Awaited so the caller's spinner hands over to the refreshed task,
        // not to a moment of the old one.
        await queryClient.invalidateQueries({ queryKey: ["tasks"] });
      } catch {
        notify("crm.plane.push_failed", { type: "error" });
      }
      return pushed;
    },
    [dataProvider, notify, queryClient],
  );
};
